import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { getLocationWithPermission } from '../utils/capacitor';
import {
  MapPinIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  MapIcon,
  BuildingOfficeIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const LocationManagement = () => {
  const [locations, setLocations] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    latitude: '',
    longitude: '',
    radius: 100,
    departmentId: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    fetchLocations();
    fetchDepartments();
  }, []);

  const fetchLocations = async () => {
    try {
      const response = await api.get('/locations');
      setLocations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching locations:', error);
      toast.error('Failed to fetch locations');
      setLoading(false);
    }
  };

  const fetchDepartments = async () => {
    try {
      const response = await api.get('/users/departments');
      const data = response.data;
      const normalized = Array.isArray(data)
        ? data
        : (Array.isArray(data?.departments) ? data.departments : []);
      setDepartments(normalized);
    } catch (error) {
      console.error('Error fetching departments:', error);
    }
  };

  const handleGetCurrentLocation = async () => {
    try {
      toast.info('Getting your current location...');
      
      const position = await getLocationWithPermission({
        enableHighAccuracy: true,
        timeout: 15000,
        maximumAge: 0
      });

      if (!position) {
        toast.error('Location permission denied or unavailable');
        return;
      }

      // Validate that coords exist and latitude/longitude are numbers
      if (!position.coords) {
        toast.error('Invalid location data received');
        console.error('Position object missing coords:', position);
        return;
      }

      // Convert to numbers (handles both string and number inputs)
      const lat = parseFloat(position.coords.latitude);
      const lng = parseFloat(position.coords.longitude);

      // Check if latitude and longitude are valid numbers
      if (isNaN(lat) || isNaN(lng) || lat === null || lng === null) {
        toast.error('Invalid coordinates received. Please try again.');
        console.error('Invalid coordinates:', { 
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          parsedLat: lat,
          parsedLng: lng,
          position: position 
        });
        return;
      }

      // Validate coordinate ranges
      if (lat < -90 || lat > 90 || lng < -180 || lng > 180) {
        toast.error('Coordinates are out of valid range. Please try again.');
        console.error('Coordinates out of range:', { lat, lng });
        return;
      }

      setFormData({
        ...formData,
        latitude: lat.toFixed(8),
        longitude: lng.toFixed(8)
      });
      toast.success('Location captured!');
    } catch (error) {
      console.error('Error getting current location:', error);
      toast.error(error.message || 'Unable to get your location. Please try again.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      // Normalize payload: send null for departmentId when "All Departments" is selected
      const payload = {
        ...formData,
        departmentId: formData.departmentId === '' ? null : formData.departmentId
      };

      if (editingLocation) {
        await api.put(`/locations/${editingLocation.id}`, payload);
        toast.success('Location updated successfully');
      } else {
        await api.post('/locations', payload);
        toast.success('Location created successfully');
      }
      
      setShowModal(false);
      setEditingLocation(null);
      resetForm();
      fetchLocations();
    } catch (error) {
      console.error('Error saving location:', error);
      toast.error(error.response?.data?.message || 'Failed to save location');
    }
  };

  const handleEdit = (location) => {
    setEditingLocation(location);
    setFormData({
      name: location.name,
      address: location.address || '',
      latitude: location.latitude,
      longitude: location.longitude,
      radius: location.radius,
      departmentId: location.departmentId || '',
      description: location.description || '',
      isActive: location.isActive
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this location?')) {
      return;
    }

    try {
      await api.delete(`/locations/${id}`);
      toast.success('Location deleted successfully');
      fetchLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      toast.error(error.response?.data?.message || 'Failed to delete location');
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      address: '',
      latitude: '',
      longitude: '',
      radius: 100,
      departmentId: '',
      description: '',
      isActive: true
    });
  };

  const openMaps = (lat, lon) => {
    window.open(`https://www.google.com/maps?q=${lat},${lon}`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6 flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Location Management</h1>
          <p className="text-gray-600 mt-1">Manage office locations for geofencing-based attendance</p>
        </div>
        <button
          onClick={() => {
            setEditingLocation(null);
            resetForm();
            setShowModal(true);
          }}
          className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Location
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {locations.map((location) => (
          <div key={location.id} className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
            <div className="flex justify-between items-start mb-4">
              <div className="flex items-center">
                <MapPinIcon className="w-6 h-6 text-green-600 mr-2" />
                <h3 className="text-lg font-semibold text-gray-900">{location.name}</h3>
              </div>
              <span className={`px-2 py-1 text-xs rounded-full ${
                location.isActive 
                  ? 'bg-green-100 text-green-800' 
                  : 'bg-red-100 text-red-800'
              }`}>
                {location.isActive ? 'Active' : 'Inactive'}
              </span>
            </div>

            {location.address && (
              <p className="text-sm text-gray-600 mb-3">{location.address}</p>
            )}

            <div className="space-y-2 mb-4">
              <div className="flex items-center text-sm">
                <BuildingOfficeIcon className="w-4 h-4 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  {location.department?.name || 'All Departments'}
                </span>
              </div>
              <div className="flex items-center text-sm">
                <MapIcon className="w-4 h-4 text-gray-400 mr-2" />
                <span className="text-gray-700">
                  Radius: {location.radius}m
                </span>
              </div>
              <div className="text-xs text-gray-500">
                Lat: {parseFloat(location.latitude).toFixed(6)}, 
                Lon: {parseFloat(location.longitude).toFixed(6)}
              </div>
            </div>

            {location.description && (
              <p className="text-sm text-gray-600 mb-4 italic">{location.description}</p>
            )}

            <div className="flex gap-2">
              <button
                onClick={() => openMaps(location.latitude, location.longitude)}
                className="flex-1 px-3 py-2 text-sm bg-blue-50 text-blue-600 rounded hover:bg-blue-100"
              >
                View on Map
              </button>
              <button
                onClick={() => handleEdit(location)}
                className="px-3 py-2 text-sm bg-gray-50 text-gray-600 rounded hover:bg-gray-100"
              >
                <PencilIcon className="w-4 h-4" />
              </button>
              <button
                onClick={() => handleDelete(location.id)}
                className="px-3 py-2 text-sm bg-red-50 text-red-600 rounded hover:bg-red-100"
              >
                <TrashIcon className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </div>

      {locations.length === 0 && (
        <div className="text-center py-12">
          <MapPinIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No locations configured yet</p>
          <p className="text-sm text-gray-500 mt-2">Add your first location to enable geofencing-based attendance</p>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-xl font-semibold text-gray-900">
                {editingLocation ? 'Edit Location' : 'Add New Location'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingLocation(null);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="w-6 h-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location Name *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Main Office, Branch 1"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Address
                </label>
                <textarea
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="2"
                  placeholder="Full address"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Latitude *
                  </label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., 28.7041"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Longitude *
                  </label>
                  <input
                    type="number"
                    step="any"
                    required
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    placeholder="e.g., 77.1025"
                  />
                </div>
              </div>

              <button
                type="button"
                onClick={handleGetCurrentLocation}
                className="w-full px-4 py-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100"
              >
                📍 Use My Current Location
              </button>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Radius (meters) *
                  </label>
                  <input
                    type="number"
                    required
                    min="10"
                    max="5000"
                    value={formData.radius}
                    onChange={(e) => setFormData({ ...formData, radius: parseInt(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Allowed distance for check-in (10-5000m)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department
                  </label>
                  <select
                    value={formData.departmentId}
                    onChange={(e) => setFormData({ ...formData, departmentId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="">All Departments</option>
                    {(Array.isArray(departments) ? departments : []).map((dept) => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  rows="2"
                  placeholder="Optional notes about this location"
                />
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <label htmlFor="isActive" className="ml-2 text-sm text-gray-700">
                  Active (users can check-in at this location)
                </label>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingLocation(null);
                    resetForm();
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  {editingLocation ? 'Update Location' : 'Create Location'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationManagement;

